import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as partnerApi from '../../api/partnerApi';

/**
 * Human-readable labels for party types
 */
const PARTY_TYPE_LABELS = {
  VENDOR: 'Vendor',
  BUYER: 'Buyer',
  CONSIGNEE: 'Consignee',
  SHIPPER: 'Shipper',
  BOOKING_AGENT: 'Booking Agent',
  NOTIFY_PARTY: 'Notify Party',
  CARRIER: 'Carrier',
  BENEFICIAL_CARGO_OWNER: 'BCO',
  ENDORSEE: 'Endorsee',
  ALSO_NOTIFY_PARTY: 'Also Notify',
  FREIGHT_PAYER: 'Freight Payer',
};

const ALL_PARTY_TYPES = Object.keys(PARTY_TYPE_LABELS);

/**
 * Empty address template for new address forms
 */
const EMPTY_ADDRESS = {
  label: '',
  addressLine1: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  partyTypes: [],
};

// ── Inline SVG icons ──────────────────────────────────────────────

function ChevronIcon({ expanded }) {
  return (
    <svg
      className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
      fill="none" stroke="currentColor" viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

/**
 * Animated collapsible container — measures content height and transitions.
 */
function Collapsible({ expanded, children }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (ref.current) {
      setHeight(ref.current.scrollHeight);
    }
  }, [expanded, children]);

  return (
    <div
      className="overflow-hidden transition-all duration-300 ease-in-out"
      style={{ maxHeight: expanded ? height : 0, opacity: expanded ? 1 : 0 }}
    >
      <div ref={ref}>
        {children}
      </div>
    </div>
  );
}

function PencilIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

// ── Address Row Component ─────────────────────────────────────────

function AddressRow({ address, onEdit, onDelete }) {
  return (
    <tr className="hover:bg-gray-50 transition-colors duration-150">
      <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{address.label || <span className="text-gray-300">—</span>}</td>
      <td className="px-4 py-2.5 text-sm text-gray-600">{address.addressLine1 || '-'}</td>
      <td className="px-4 py-2.5 text-sm text-gray-600">{address.city || '-'}</td>
      <td className="px-4 py-2.5 text-sm text-gray-400">{address.state || '-'}</td>
      <td className="px-4 py-2.5 text-sm text-gray-600">{address.postalCode || '-'}</td>
      <td className="px-4 py-2.5 text-sm text-gray-600">{address.country || '-'}</td>
      <td className="px-4 py-2 text-sm">
        <div className="flex flex-wrap gap-1">
          {(address.partyTypes || []).map((pt) => (
            <span key={pt} className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700">
              {PARTY_TYPE_LABELS[pt] || pt}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-2 text-sm">
        <div className="flex items-center gap-2">
          <button onClick={onEdit} className="text-gray-400 hover:text-gray-600">
            <PencilIcon />
          </button>
          <button onClick={onDelete} className="text-gray-400 hover:text-red-500">
            <TrashIcon />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Address Form Row Component ────────────────────────────────────

function AddressFormRow({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_ADDRESS);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const togglePartyType = (pt) => {
    setForm((prev) => {
      const types = prev.partyTypes || [];
      return {
        ...prev,
        partyTypes: types.includes(pt)
          ? types.filter((t) => t !== pt)
          : [...types, pt],
      };
    });
  };

  return (
    <tr className="bg-blue-50/40">
      <td className="px-4 py-2">
        <input
          type="text" placeholder="Label" value={form.label}
          onChange={(e) => handleChange('label', e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </td>
      <td className="px-4 py-2">
        <input
          type="text" placeholder="Address" value={form.addressLine1}
          onChange={(e) => handleChange('addressLine1', e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </td>
      <td className="px-4 py-2">
        <input
          type="text" placeholder="City" value={form.city}
          onChange={(e) => handleChange('city', e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </td>
      <td className="px-4 py-2">
        <input
          type="text" placeholder="State" value={form.state}
          onChange={(e) => handleChange('state', e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </td>
      <td className="px-4 py-2">
        <input
          type="text" placeholder="Postal" value={form.postalCode}
          onChange={(e) => handleChange('postalCode', e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </td>
      <td className="px-4 py-2">
        <input
          type="text" placeholder="Country" value={form.country}
          onChange={(e) => handleChange('country', e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-wrap gap-1">
          {ALL_PARTY_TYPES.map((pt) => (
            <button
              key={pt}
              type="button"
              onClick={() => togglePartyType(pt)}
              className={`px-1.5 py-0.5 text-[10px] font-medium rounded transition-colors ${
                (form.partyTypes || []).includes(pt)
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {PARTY_TYPE_LABELS[pt]}
            </button>
          ))}
        </div>
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onSave(form)}
            className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            Save
          </button>
          <button
            onClick={onCancel}
            className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Address Table Component ───────────────────────────────────────

function AddressTable({ addresses, onEdit, onDelete, onAdd }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const handleSaveNew = async (form) => {
    await onAdd(form);
    setShowAddForm(false);
  };

  const handleSaveEdit = async (form) => {
    await onEdit(editingId, form);
    setEditingId(null);
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">Label</th>
              <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">Address</th>
              <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">City</th>
              <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">State</th>
              <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">Postal</th>
              <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">Country</th>
              <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">Party Types</th>
              <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(!addresses || addresses.length === 0) && !showAddForm && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-400">
                  No addresses yet
                </td>
              </tr>
            )}
            {(addresses || []).map((addr) =>
              editingId === addr._id ? (
                <AddressFormRow
                  key={addr._id}
                  initial={addr}
                  onSave={handleSaveEdit}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <AddressRow
                  key={addr._id}
                  address={addr}
                  onEdit={() => setEditingId(addr._id)}
                  onDelete={() => onDelete(addr._id)}
                />
              )
            )}
            {showAddForm && (
              <AddressFormRow
                onSave={handleSaveNew}
                onCancel={() => setShowAddForm(false)}
              />
            )}
          </tbody>
        </table>
      </div>
      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="mt-3 px-3 py-1.5 text-xs font-medium text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50"
        >
          + Add Address
        </button>
      )}
    </div>
  );
}

// ── Aliases Component ─────────────────────────────────────────────

function AliasesBadges({ aliases, onRemove, onAdd }) {
  const [showInput, setShowInput] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showInput]);

  const handleAdd = async () => {
    if (!value.trim()) return;
    await onAdd(value.trim());
    setValue('');
    setShowInput(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    } else if (e.key === 'Escape') {
      setShowInput(false);
      setValue('');
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {(aliases || []).map((alias) => (
        <span
          key={alias}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-md"
        >
          {alias}
          <button
            onClick={() => onRemove(alias)}
            className="text-gray-400 hover:text-red-500 ml-0.5"
          >
            &times;
          </button>
        </span>
      ))}
      {showInput ? (
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (!value.trim()) setShowInput(false); }}
          placeholder="Type alias..."
          className="px-2 py-0.5 text-xs border border-blue-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 w-32"
        />
      ) : (
        <button
          onClick={() => setShowInput(true)}
          className="px-2 py-0.5 text-xs text-blue-600 hover:text-blue-800 border border-dashed border-blue-300 rounded-md hover:bg-blue-50"
        >
          + Add Alias
        </button>
      )}
    </div>
  );
}

// ── Subdivision Card Component ────────────────────────────────────

function SubdivisionCard({ subdivision, partnerId, expanded, onToggle, onRefresh, onError }) {
  /**
   * Collect unique party types across all addresses in this subdivision
   */
  const partyTypes = [...new Set(
    (subdivision.addresses || []).flatMap((a) => a.partyTypes || [])
  )];

  const addressCount = (subdivision.addresses || []).length;

  // ── Alias handlers ──────────────────────────────────────────────
  const handleAddAlias = async (alias) => {
    try {
      await partnerApi.addSubdivisionAlias(partnerId, subdivision._id, alias);
      onRefresh();
    } catch (err) {
      onError(err.message || 'Failed to add subdivision alias');
    }
  };

  const handleRemoveAlias = async (alias) => {
    try {
      await partnerApi.removeSubdivisionAlias(partnerId, subdivision._id, alias);
      onRefresh();
    } catch (err) {
      onError(err.message || 'Failed to remove subdivision alias');
    }
  };

  // ── Address handlers ────────────────────────────────────────────
  const handleAddAddress = async (address) => {
    try {
      await partnerApi.addSubdivisionAddress(partnerId, subdivision._id, address);
      onRefresh();
    } catch (err) {
      onError(err.message || 'Failed to add address');
    }
  };

  const handleEditAddress = async (addrId, data) => {
    try {
      await partnerApi.updateSubdivisionAddress(partnerId, subdivision._id, addrId, data);
      onRefresh();
    } catch (err) {
      onError(err.message || 'Failed to update address');
    }
  };

  const handleDeleteAddress = async (addrId) => {
    try {
      await partnerApi.removeSubdivisionAddress(partnerId, subdivision._id, addrId);
      onRefresh();
    } catch (err) {
      onError(err.message || 'Failed to delete address');
    }
  };

  const handleDeleteSubdivision = async () => {
    if (!window.confirm(`Delete subdivision "${subdivision.name}"?`)) return;
    try {
      await partnerApi.removeSubdivision(partnerId, subdivision._id);
      onRefresh();
    } catch (err) {
      onError(err.message || 'Failed to delete subdivision');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Subdivision header — always visible */}
      <div
        className="flex items-center gap-3 px-5 py-4 cursor-pointer select-none hover:bg-gray-50 transition-colors duration-150 rounded-t-xl"
        onClick={onToggle}
      >
        <ChevronIcon expanded={expanded} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-900">{subdivision.name}</h3>
            <div className="flex gap-1">
              {partyTypes.map((pt) => (
                <span key={pt} className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700">
                  {PARTY_TYPE_LABELS[pt] || pt}
                </span>
              ))}
            </div>
            <span className="text-xs text-gray-400">
              {addressCount} {addressCount === 1 ? 'address' : 'addresses'}
            </span>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); handleDeleteSubdivision(); }}
          className="text-gray-400 hover:text-red-500"
        >
          <TrashIcon />
        </button>
      </div>

      {/* Expanded content with smooth animation */}
      <Collapsible expanded={expanded}>
        <div className="px-5 pb-4 border-t border-gray-100 pt-3">
          {/* Subdivision aliases */}
          <div className="mb-4">
            <span className="text-xs font-medium text-gray-500 mr-2">Aliases:</span>
            <AliasesBadges
              aliases={subdivision.aliases}
              onAdd={handleAddAlias}
              onRemove={handleRemoveAlias}
            />
          </div>

          {/* Address table */}
          <AddressTable
            addresses={subdivision.addresses}
            onAdd={handleAddAddress}
            onEdit={handleEditAddress}
            onDelete={handleDeleteAddress}
          />
        </div>
      </Collapsible>
    </div>
  );
}

// ── Adopt Partner Dropdown ────────────────────────────────────────

function AdoptPartnerDropdown({ partnerId, onAdopt, onClose }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // Search partners as user types
  useEffect(() => {
    if (!search.trim()) {
      setResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await partnerApi.getPartners({ search: search.trim() });
        // Filter out the current partner from results
        const filtered = (res.data || []).filter((p) => p._id !== partnerId);
        setResults(filtered);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [search, partnerId]);

  return (
    <div className="absolute z-20 mt-1 w-72 bg-white rounded-lg border border-gray-200 shadow-lg">
      <div className="p-2">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search partners..."
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
      <div className="max-h-48 overflow-y-auto">
        {loading && (
          <div className="px-3 py-2 text-xs text-gray-400">Searching...</div>
        )}
        {!loading && search.trim() && results.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No partners found</div>
        )}
        {results.map((p) => (
          <button
            key={p._id}
            onClick={() => onAdopt(p._id)}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center justify-between"
          >
            <span>{p.name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
              p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {p.status === 'active' ? 'Active' : 'Pending'}
            </span>
          </button>
        ))}
      </div>
      <div className="border-t border-gray-100 p-2">
        <button
          onClick={onClose}
          className="w-full px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────

/**
 * PartnerDetail Page
 *
 * Full detail page for viewing and managing a single partner,
 * including aliases, addresses, subdivisions, and adoption.
 */
function PartnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSubs, setExpandedSubs] = useState(new Set());
  const [showAdopt, setShowAdopt] = useState(false);
  const [showAddSub, setShowAddSub] = useState(false);
  const [newSubName, setNewSubName] = useState('');

  /**
   * Fetch the partner from the API
   */
  const fetchPartner = useCallback(async () => {
    try {
      setError('');
      const res = await partnerApi.getPartner(id);
      setPartner(res.data);
    } catch (err) {
      setError(err.message || 'Failed to load partner');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPartner();
  }, [fetchPartner]);

  // ── Alias handlers (partner-level) ──────────────────────────────

  const handleAddAlias = async (alias) => {
    try {
      await partnerApi.addPartnerAlias(id, alias);
      fetchPartner();
    } catch (err) {
      setError(err.message || 'Failed to add alias');
    }
  };

  const handleRemoveAlias = async (alias) => {
    try {
      await partnerApi.removePartnerAlias(id, alias);
      fetchPartner();
    } catch (err) {
      setError(err.message || 'Failed to remove alias');
    }
  };

  // ── Address handlers (partner-level, for standalone partners) ───

  const handleAddAddress = async (address) => {
    try {
      await partnerApi.addPartnerAddress(id, address);
      fetchPartner();
    } catch (err) {
      setError(err.message || 'Failed to add address');
    }
  };

  const handleEditAddress = async (addrId, data) => {
    try {
      await partnerApi.updatePartnerAddress(id, addrId, data);
      fetchPartner();
    } catch (err) {
      setError(err.message || 'Failed to update address');
    }
  };

  const handleDeleteAddress = async (addrId) => {
    try {
      await partnerApi.removePartnerAddress(id, addrId);
      fetchPartner();
    } catch (err) {
      setError(err.message || 'Failed to delete address');
    }
  };

  // ── Subdivision handlers ────────────────────────────────────────

  const handleAddSubdivision = async (e) => {
    e.preventDefault();
    if (!newSubName.trim()) return;
    try {
      await partnerApi.addSubdivision(id, { name: newSubName.trim() });
      setNewSubName('');
      setShowAddSub(false);
      fetchPartner();
    } catch (err) {
      setError(err.message || 'Failed to add subdivision');
    }
  };

  // ── Adopt handler ───────────────────────────────────────────────

  const handleAdopt = async (childId) => {
    try {
      await partnerApi.adoptPartner(id, childId);
      setShowAdopt(false);
      fetchPartner();
    } catch (err) {
      setError(err.message || 'Failed to adopt partner');
    }
  };

  // ── Status handler ──────────────────────────────────────────────

  const handleActivate = async () => {
    try {
      await partnerApi.updatePartnerStatus(id, 'active');
      fetchPartner();
    } catch (err) {
      setError(err.message || 'Failed to activate partner');
    }
  };

  // ── Toggle subdivision expansion ────────────────────────────────

  const toggleSub = (subId) => {
    setExpandedSubs((prev) => {
      const next = new Set(prev);
      if (next.has(subId)) {
        next.delete(subId);
      } else {
        next.add(subId);
      }
      return next;
    });
  };

  // ── Delete partner ──────────────────────────────────────────────

  const handleDeletePartner = async () => {
    if (!window.confirm(`Delete partner "${partner.name}"? This cannot be undone.`)) return;
    try {
      await partnerApi.deletePartner(id);
      navigate('/admin/partners');
    } catch (err) {
      setError(err.message || 'Failed to delete partner');
    }
  };

  // ── Determine if partner has subdivisions ───────────────────────

  const hasSubdivisions = partner?.subdivisions?.length > 0;

  // ── Render ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!partner && !loading) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">{error || 'Partner not found'}</p>
        <Link to="/admin/partners" className="mt-4 inline-block text-sm text-blue-600 hover:text-blue-800">
          Back to Partners
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-4">
        <Link to="/admin/partners" className="hover:text-blue-600">Partners</Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">{partner.name}</span>
      </nav>

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}

      {/* ── Partner Header Card ──────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
        {/* Top row: name, status, delete */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900">{partner.name}</h1>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              partner.status === 'active'
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              {partner.status === 'active' ? 'Active' : 'Pending Review'}
            </span>
          </div>
          <button
            onClick={handleDeletePartner}
            className="text-gray-400 hover:text-red-500"
            title="Delete partner"
          >
            <TrashIcon />
          </button>
        </div>

        {/* Aliases row */}
        <div className="mb-4">
          <AliasesBadges
            aliases={partner.aliases}
            onAdd={handleAddAlias}
            onRemove={handleRemoveAlias}
          />
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3 relative">
          {/* Adopt Existing Partner */}
          <div className="relative">
            <button
              onClick={() => setShowAdopt(!showAdopt)}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Adopt Existing Partner
            </button>
            {showAdopt && (
              <AdoptPartnerDropdown
                partnerId={id}
                onAdopt={handleAdopt}
                onClose={() => setShowAdopt(false)}
              />
            )}
          </div>

          {/* Add Subdivision */}
          <button
            onClick={() => setShowAddSub(true)}
            className="px-3 py-1.5 text-xs font-medium text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50"
          >
            + Add Subdivision
          </button>

          {/* Activate button (only for pending_review) */}
          {partner.status === 'pending_review' && (
            <button
              onClick={handleActivate}
              className="px-3 py-1.5 text-xs font-medium text-emerald-700 border border-emerald-300 rounded-lg hover:bg-emerald-50"
            >
              Activate
            </button>
          )}
        </div>

        {/* Add Subdivision inline form */}
        {showAddSub && (
          <form onSubmit={handleAddSubdivision} className="mt-4 flex items-center gap-2">
            <input
              type="text"
              value={newSubName}
              onChange={(e) => setNewSubName(e.target.value)}
              placeholder="Subdivision name..."
              autoFocus
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 w-64"
            />
            <button
              type="submit"
              disabled={!newSubName.trim()}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => { setShowAddSub(false); setNewSubName(''); }}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
          </form>
        )}
      </div>

      {/* ── Standalone Addresses (no subdivisions) ───────────────── */}
      {!hasSubdivisions && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Addresses</h2>
          <AddressTable
            addresses={partner.addresses}
            onAdd={handleAddAddress}
            onEdit={handleEditAddress}
            onDelete={handleDeleteAddress}
          />
        </div>
      )}

      {/* ── Subdivision Cards ────────────────────────────────────── */}
      {hasSubdivisions && (
        <div className="space-y-3">
          {partner.subdivisions.map((sub) => (
            <SubdivisionCard
              key={sub._id}
              subdivision={sub}
              partnerId={id}
              expanded={expandedSubs.has(sub._id)}
              onToggle={() => toggleSub(sub._id)}
              onRefresh={fetchPartner}
              onError={setError}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default PartnerDetail;
